import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  CircleCheckIcon,
  EllipsisIcon,
  PinIcon,
  PinOffIcon,
  Trash2Icon
} from 'lucide-react';
import type { FC } from 'react';
import { cn } from '../lib/utils';
import { ChatHistoryActionButton } from './chat-history-action-button';
import type { ChatHistoryItem } from './chat-history-groups';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './dropdown-menu';

interface ChatHistoryRowProps {
  item: ChatHistoryItem;
  isActive: boolean;
  onSelect: (id: string) => void;
  onTogglePin?: (id: string) => void;
  onArchive?: (id: string, archived: boolean) => void;
  onDelete?: (id: string) => void;
}

/**
 * One past conversation. The title opens it; pin, archive and the "more"
 * menu sit beside it and show on hover or keyboard focus.
 */
export const ChatHistoryRow: FC<ChatHistoryRowProps> = ({
  item,
  isActive,
  onSelect,
  onTogglePin,
  onArchive,
  onDelete
}) => (
  <li
    className={cn(
      'group/history-row flex items-center gap-1 rounded-lg pr-1 transition-colors hover:bg-surface-hover has-[:focus-visible]:bg-surface-hover',
      isActive && 'bg-surface-hover'
    )}
  >
    <button
      type="button"
      aria-current={isActive ? 'true' : undefined}
      onClick={() => onSelect(item.id)}
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-lg py-2 pl-2.5 text-left text-body text-[var(--z-text-secondary)] outline-none hover:text-foreground focus-visible:text-foreground aria-[current]:text-foreground"
    >
      <CircleCheckIcon
        aria-hidden="true"
        className={cn('size-4 shrink-0 text-muted-foreground', isActive && 'text-brand')}
      />
      <span className="truncate">{item.title}</span>
    </button>
    <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover/history-row:opacity-100 group-has-[:focus-visible]/history-row:opacity-100 has-[[aria-expanded=true]]:opacity-100 [@media(hover:none)]:opacity-100">
      {onDelete ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ChatHistoryActionButton label={`More for ${item.title}`}>
              <EllipsisIcon />
            </ChatHistoryActionButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem variant="destructive" onSelect={() => onDelete(item.id)}>
              <Trash2Icon />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {onTogglePin && !item.archived ? (
        <ChatHistoryActionButton
          label={item.pinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
          onClick={() => onTogglePin(item.id)}
        >
          {item.pinned ? <PinOffIcon /> : <PinIcon />}
        </ChatHistoryActionButton>
      ) : null}
      {onArchive ? (
        <ChatHistoryActionButton
          label={item.archived ? `Restore ${item.title}` : `Archive ${item.title}`}
          onClick={() => onArchive(item.id, !item.archived)}
        >
          {item.archived ? <ArchiveRestoreIcon /> : <ArchiveIcon />}
        </ChatHistoryActionButton>
      ) : null}
    </div>
  </li>
);
